<template>
  <v-card
    class="product-card d-flex flex-column"
    elevation="2"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <!-- Imagen -->
    <div class="product-image-container" @click="openDetails">
      <v-img
        :src="img"
        :alt="name"
        height="270"
        contain
        class="product-image"
      >
        <template v-slot:placeholder>
          <v-row
            class="fill-height ma-0"
            align="center"
            justify="center"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </v-row>
        </template>
      </v-img>

      <v-chip
        v-if="stock > 0 && stock <= 5"
        small
        color="orange darken-2"
        text-color="white"
        class="stock-chip"
      >
        Últimas {{ stock }} unidades
      </v-chip>

      <v-chip
        v-if="stock <= 0"
        small
        color="grey darken-1"
        text-color="white"
        class="stock-chip"
      >
        Agotado
      </v-chip>
    </div>

    <!-- Información -->
    <v-card-text class="product-information flex-grow-1">
      <div
        v-if="brand"
        class="product-brand"
      >
        {{ brand }}
      </div>

      <h3 class="product-name">
        {{ name }}
      </h3>

      <p
        v-if="description"
        class="product-description"
      >
        {{ description }}
      </p>

      <div class="personalizable-label">
        <v-icon small color="primary" class="mr-1">
          mdi-palette-outline
        </v-icon>

        Producto personalizable
      </div>

      <div class="product-price">
        {{ formattedPrice }}
      </div>
    </v-card-text>

    <!-- Acciones -->
    <v-card-actions class="product-actions">
      <v-btn
        block
        color="primary"
        elevation="0"
        class="details-button"
        :disabled="stock <= 0"
        @click="openDetails"
      >
        <v-icon left small>
          mdi-eye-outline
        </v-icon>

        Ver y personalizar
      </v-btn>
    </v-card-actions>

    <!-- Modal -->
    <v-dialog
      v-model="showDialog"
      max-width="900"
      scrollable
    >
      <v-card class="product-dialog">
        <v-toolbar
          color="primary"
          dark
          flat
          dense
        >
          <v-toolbar-title class="dialog-title">
            {{ name }}
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            aria-label="Cerrar"
            @click="showDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-0">
          <v-container class="pa-4 pa-md-7">
            <v-row>
              <!-- Imagen del producto -->
              <v-col
                cols="12"
                md="6"
                class="d-flex justify-center align-center"
              >
                <div class="dialog-image-container">
                  <v-img
                    :src="img"
                    :alt="name"
                    contain
                    max-height="480"
                  />
                </div>
              </v-col>

              <!-- Detalles -->
              <v-col
                cols="12"
                md="6"
                class="product-dialog-information"
              >
                <div
                  v-if="brand"
                  class="dialog-brand"
                >
                  {{ brand }}
                </div>

                <h2 class="dialog-product-name">
                  {{ name }}
                </h2>

                <div class="dialog-price">
                  {{ formattedPrice }}
                </div>

                <v-divider class="my-5" />

                <p
                  v-if="description"
                  class="dialog-description"
                >
                  {{ description }}
                </p>

                <v-alert
                  dense
                  text
                  color="primary"
                  icon="mdi-palette-outline"
                  class="mb-5"
                >
                  Este producto puede personalizarse con nombres, fotos o diseños.
                </v-alert>

                <div class="quantity-label">
                  Cantidad
                </div>

                <div class="quantity-control">
                  <v-btn
                    icon
                    outlined
                    small
                    :disabled="quantitySelected <= 1"
                    @click="decreaseQuantity"
                  >
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>

                  <span class="quantity-value">
                    {{ quantitySelected }}
                  </span>

                  <v-btn
                    icon
                    outlined
                    small
                    :disabled="quantitySelected >= maximumQuantity"
                    @click="increaseQuantity"
                  >
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </div>

                <div class="total-container">
                  <span>Total</span>

                  <strong>
                    {{ formattedTotalPrice }}
                  </strong>
                </div>

                <v-btn
                  block
                  large
                  color="primary"
                  dark
                  elevation="0"
                  class="add-cart-button"
                  :loading="addingToCart"
                  :disabled="stock <= 0"
                  @click="addToCart"
                >
                  <v-icon left>
                    mdi-cart-plus
                  </v-icon>

                  Agregar al carrito
                </v-btn>

                <div class="purchase-benefits">
                  <div>
                    <v-icon small color="success">
                      mdi-check-circle-outline
                    </v-icon>
                    Diseño personalizado
                  </div>

                  <div>
                    <v-icon small color="success">
                      mdi-check-circle-outline
                    </v-icon>
                    Confirmación antes de producir
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: "ProductCard",

  props: {
    product_id: {
      type: [Number, String],
      required: true,
    },
    brand: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    price: {
      type: [Number, String],
      required: true,
    },
    stock: {
      type: Number,
      default: 0,
    },
    img: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      quantitySelected: 1,
      showDialog: false,
      addingToCart: false,
      isHovering: false,
    };
  },

  computed: {
    numericPrice() {
      const value = Number(this.price);
      return Number.isNaN(value) ? 0 : value;
    },

    maximumQuantity() {
      return Math.max(Number(this.stock) || 0, 1);
    },

    totalPrice() {
      return this.numericPrice * this.quantitySelected;
    },

    formattedPrice() {
      return this.formatCurrency(this.numericPrice);
    },

    formattedTotalPrice() {
      return this.formatCurrency(this.totalPrice);
    },
  },

  watch: {
    showDialog(isOpen) {
      if (isOpen) {
        this.quantitySelected = 1;
      }
    },
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat("es-VE", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(value);
    },

    openDetails() {
      if (this.stock > 0) {
        this.showDialog = true;
      }
    },

    increaseQuantity() {
      if (this.quantitySelected < this.maximumQuantity) {
        this.quantitySelected += 1;
      }
    },

    decreaseQuantity() {
      if (this.quantitySelected > 1) {
        this.quantitySelected -= 1;
      }
    },

    async addToCart() {
      const newProduct = {
        product_id: this.product_id,
        brand: this.brand,
        name: this.name,
        price: this.numericPrice,
        quantity: this.quantitySelected,
        img: this.img,
      };

      this.addingToCart = true;

      try {
        await this.$store.dispatch("addShoppingCart", newProduct);

        this.$store.commit("showSnackbar", {
          text: "Producto agregado con éxito",
          color: "success",
        });

        this.showDialog = false;
      } catch (error) {
        console.error("Error agregando el producto al carrito:", error);

        this.$store.commit("showSnackbar", {
          text: "No fue posible agregar el producto al carrito",
          color: "error",
        });
      } finally {
        this.addingToCart = false;
      }
    },
  },
};
</script>

<style scoped>
.product-card {
  height: 100%;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid #eeeeee;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 28px rgba(40, 20, 70, 0.15) !important;
}

.product-image-container {
  position: relative;
  overflow: hidden;
  background: #f7f7f9;
  cursor: pointer;
}

.product-image {
  transition: transform 0.35s ease;
}

.product-card:hover .product-image {
  transform: scale(1.03);
}

.stock-chip {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 2;
}

.product-information {
  padding: 18px 18px 10px;
}

.product-brand {
  min-height: 18px;
  margin-bottom: 3px;
  color: #777777;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-name {
  min-height: 48px;
  margin: 0 0 8px;
  color: #252525;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.product-description {
  display: -webkit-box;
  min-height: 42px;
  margin: 0 0 12px;
  overflow: hidden;
  color: #686868;
  font-size: 13px;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.personalizable-label {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #6b27b4;
  font-size: 13px;
  font-weight: 600;
}

.product-price {
  color: #5d1591;
  font-size: 24px;
  font-weight: 800;
}

.product-actions {
  padding: 12px 18px 18px;
}

.details-button,
.add-cart-button {
  border-radius: 8px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: none;
}

.dialog-title {
  max-width: calc(100% - 60px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-image-container {
  width: 100%;
  min-height: 390px;
  padding: 20px;
  background: #f7f7f9;
  border-radius: 14px;
}

.product-dialog-information {
  padding: 20px 28px;
}

.dialog-brand {
  margin-bottom: 5px;
  color: #777777;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dialog-product-name {
  margin-bottom: 10px;
  color: #222222;
  font-size: 28px;
  line-height: 1.25;
}

.dialog-price {
  color: #5d1591;
  font-size: 30px;
  font-weight: 800;
}

.dialog-description {
  color: #626262;
  font-size: 15px;
  line-height: 1.7;
}

.quantity-label {
  margin-bottom: 8px;
  color: #333333;
  font-size: 14px;
  font-weight: 700;
}

.quantity-control {
  display: flex;
  align-items: center;
  margin-bottom: 25px;
}

.quantity-value {
  min-width: 50px;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
}

.total-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 15px 0;
  border-top: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  font-size: 18px;
}

.total-container strong {
  color: #5d1591;
  font-size: 24px;
}

.purchase-benefits {
  margin-top: 18px;
  color: #606060;
  font-size: 13px;
  line-height: 2;
}

@media (max-width: 600px) {
  .product-image {
    height: 230px !important;
  }

  .product-name {
    min-height: auto;
    font-size: 17px;
  }

  .dialog-image-container {
    min-height: 280px;
  }

  .product-dialog-information {
    padding: 15px 12px;
  }

  .dialog-product-name {
    font-size: 23px;
  }

  .dialog-price {
    font-size: 26px;
  }
}
</style>